import React, { SetStateAction, useEffect, useMemo, useState } from "react";
import { RiSearchLine } from "react-icons/ri";
import { useNavigate, useParams } from "react-router-dom";
import BaseButton from "Components/Base/BaseButton";
import TableContainer from "Components/Base/BaseTable";
import {
  checkStatusCodeSuccess,
  notFound,
  Search,
} from "Components/constants/Common";
import { toast } from "react-toastify";
import {
  CREATED_ON,
  ISSUE_QTY_LABEL,
  ISSUE_TYPE_LABEL,
  ITEM_CODE_LABEL,
  PENDING_QTY_LABEL,
  RECEIVERS_NAME,
  REQUESTED_QTY_LABEL,
  RETURN_SPARE_PART,
  SR_NO_LABEL,
} from "Components/constants/ROModule";
import { historyOfPartIssueDetailsLabel } from "Components/constants/PartsIssueDetails";
import { historyOfPID } from "Api/PartIssueDetails";

const PartIssueHistory = () => {
  const [loader, setLoader] = useState<boolean>(false);
  const [ROO, setROO] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState<number>(0);
  const navigate = useNavigate();
  const [sortOrder, setSortOrder] = useState<string>("desc");
  const [ROOSearchData, setROOSearchData] = useState<Array<any>>([]);
  const [columnName, setColumnName] = useState<string>("id");
  const { id } = useParams<{ id: string }>();

  const formatDate = (date: string) => {
    const d = new Date(date);
    const day = d.getDate();
    const month = d.toLocaleString('default', { month: 'short' }); 
    const year = d.getFullYear();
    const hours = d.getHours();
    const minutes = d.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12; 
    const formattedMinutes = minutes < 10 ? '0' + minutes : minutes;
    return `${month} ${day}, ${year}, ${formattedHours}:${formattedMinutes} ${ampm}`;
  };

  const handleFetchData = (page: any) => {
    setCurrentPage(page);
  };

  const handleFetchSorting = (
    page: any,
    id: SetStateAction<string>,
    order: SetStateAction<string>
  ) => {
    setCurrentPage(page);

    if (id === "product.product_code") {
      setColumnName("product_id");
    } else {
      setColumnName(id);
    }
    setSortOrder(order);
  };

  const fetchROOList = async () => {
    const payload = {
      limit,
      page: currentPage,
      sortKey: columnName,
      sortValue: sortOrder,
    };
    setLoader(true);
    historyOfPID(Number(id),payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setROO(res?.data?.partIssueHistory);
          setTotalRecords(res?.data?.totalItems || 0);
          setTotalPages(res?.data?.totalPages);
          setCurrentPage(res?.data?.currentPage);
          setTotalNumberOfRows(res?.data?.numberOfRows);
        } else {
          setROO([]);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message);
      })
      .finally(() => setLoader(false));
  };

  const fetchSearchROOData = () => {
    const payload = {
      limit,
      page: currentPage,
      search: searchValue,
      sortKey: columnName,
      sortValue: sortOrder,
    };
    setLoader(true);
    historyOfPID(Number(id),payload)
    .then((res) => {
      if (checkStatusCodeSuccess(res?.statusCode)) {
        setROOSearchData(res?.data?.partIssueHistory);
        setTotalRecords(res?.data?.totalItems || 0);
        setTotalPages(res?.data?.totalPages);
        setCurrentPage(res?.data?.currentPage);
        setTotalNumberOfRows(res?.data?.numberOfRows);
        } else {
          setROOSearchData([]);
        }
      })
      .catch((error) => {
        setROOSearchData([]);
      })
      .finally(() => {
        setLoader(false);
      });
  };


  useEffect(() => {
    if (searchValue) {
      fetchSearchROOData();
    } else {
      fetchROOList();
    }
  }, [currentPage, limit, searchValue, sortOrder, columnName]);

  const repairOrderColums = useMemo(
    () => [
      {
        header: SR_NO_LABEL,
        accessorKey: "srNo",
        cell: (info: any) => info.row.index + 1,
        flex: 1.5,
        minWidth: 150,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: ITEM_CODE_LABEL,
        accessorKey: "product.product_code",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: REQUESTED_QTY_LABEL,
        accessorKey: "request_quantity",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: ISSUE_QTY_LABEL,
        accessorKey: "issue_quantity",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: PENDING_QTY_LABEL,
        accessorKey: "pending_quantity",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: ISSUE_TYPE_LABEL,
        accessorKey: "issue_type",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: RECEIVERS_NAME,
        accessorKey: "receiver_name",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: RETURN_SPARE_PART,
        accessorKey: "return_spare_part",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: CREATED_ON,
        accessorKey: "createdAt",
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell: any) => {
          return formatDate(cell?.row?.original?.createdAt); 
        },
      },
    ],
    []
  );

  document.title = historyOfPartIssueDetailsLabel;

  return (
    <div className="page-content">
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4 d-flex gap-4">
            {historyOfPartIssueDetailsLabel}
          </span>
          <div className="d-flex align-items-center gap-2">
            <BaseButton
              className="btn btn-danger"
              onClick={() => navigate("/partIssueDetails")}
              children={
                <>
                  <i className="mdi mdi-arrow-left" />
                </>
              }
            />
          </div>
        </div>

        <div className="card py-2 px-4 overflow-x-auto">
          {loader || ROO.length > 0 || ROOSearchData.length > 0 ? (
            <TableContainer
              customPageSize={limit}
              setCustomPageSize={setLimit}
              isGlobalFilter={true}
              columns={repairOrderColums}
              data={searchValue ? ROOSearchData : ROO || []}
              tableClass="table bg-white"
              manualPagination={true}
              divClass="text-nowrap"
              manualFiltering={true}
              tableLoader={loader}
              hasManualPagination={true}
              SearchPlaceholder={Search}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              totalNumberOfRows={totalNumberOfRows}
              onSearch={setSearchValue}
              fetchData={handleFetchData}
              fetchSortingData={handleFetchSorting}
            />
          ) : (
            <div className="py-4 text-center">
              <div>
                <span className="fs-1 text-success">
                  <RiSearchLine />
                </span>
              </div>
              <div className="mt-4">
                <h5>{notFound.dataNotFound}</h5>
                <p className="text-muted">{notFound.thereIsNoDataFound}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartIssueHistory;
